/**
 * Stand-in for the Auth0 SPA client when the API's local dev issuer is enabled
 * (`provider: 'dev'` from /api/config/auth0). It implements only the client methods auth.js
 * uses. Sign-in happens on the server's /dev/login page, which stores the session below.
 */
export const DEV_AUTH_STORAGE_KEY = 'ironsight.devAuth'

const SIGN_IN_PATH = '/dev/login'

export interface DevAuthSession {
  access_token: string
  expires_at: string
  user: Record<string, unknown>
}

interface DevAuthClientDependencies {
  storage?: Pick<Storage, 'getItem' | 'removeItem'>
  navigate?: (url: string) => void
  now?: () => number
}

interface LoginOptions {
  appState?: { targetUrl?: string }
}

export function createDevAuthClient({
  storage = window.localStorage,
  navigate = (url) => window.location.assign(url),
  now = () => Date.now(),
}: DevAuthClientDependencies = {}) {
  function session(): DevAuthSession | undefined {
    const stored = storage.getItem(DEV_AUTH_STORAGE_KEY)
    if (!stored) return undefined

    const parsed: DevAuthSession = JSON.parse(stored)
    return Date.parse(parsed.expires_at) > now() ? parsed : undefined
  }

  return {
    // auth.js reads options for password resets, which dev sign-in has no equivalent of.
    options: {},

    async isAuthenticated() {
      return session() !== undefined
    },

    async getUser() {
      return session()?.user
    },

    async getIdTokenClaims() {
      return session()?.user
    },

    async getTokenSilently() {
      const current = session()
      if (!current) {
        throw Object.assign(new Error('Dev sign-in required'), { error: 'login_required' })
      }
      return current.access_token
    },

    async loginWithRedirect(options?: LoginOptions) {
      const returnTo = options?.appState?.targetUrl ?? '/'
      navigate(`${SIGN_IN_PATH}?returnTo=${encodeURIComponent(returnTo)}`)
    },

    async handleRedirectCallback() {
      return { appState: undefined }
    },

    async logout() {
      storage.removeItem(DEV_AUTH_STORAGE_KEY)
      navigate(SIGN_IN_PATH)
    },
  }
}
